import { useState, useEffect } from 'react'
import './App.css'
import { AdapterMoment} from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers'
import {
    Login,
    Signup,
    SubmitShift,
    SelectStore,
    SelectMonth,
    Calendar,
    Home,
    CheckFinalizedMonths,
    CheckFinalizedCalendar,
    CheckShiftDay,
    RecurringShifts,
    ManagerSettings,
    CreateShiftStores,
    CreateShiftMonths,
    SubmittedEmployees,
    UserSettings,
    ForgotPassword,
    ResetPassword,
    MonthlyShiftHeader,
    EmailConfirmation,
    AdminSettings,
    ResendConfirmation,
    ViewOnlyHome,
    ShiftManagements,
    FeedbackManagements,
    FeedbackManagementsDemo,
    Layout,
} from './Components'
import AdminRoute from './Components/AdminRoute/AdminRoute'
import { PATHS } from './constants'
import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom'
import { EmploymentsContext } from './contexts/EmploymentsContext'
import { AxiosWithAuth, parseStoredCurrentUser, STORE_EMPLOYMENTS_CHANGED_EVENT } from './Utilities/authenticationService'
import chatScheduler from './Components/openai-stuff/partTimerScheduler'
import { useVersionCheck } from './Utilities/useVersionCheck'
import { Snackbar, Button } from '@mui/material'

function App() {
    const [userEmployments, setUserEmployments] = useState([])
    const [isManager, setIsManager] = useState(false)
    const [isManagerFor, setIsManagerFor] = useState({})
    const [canEditShift, setCanEditShift] = useState(false)
    const [canEditShiftFor, setCanEditShiftFor] = useState({})
    const [userInfo, setUserInfo] = useState({})
    const [isAdmin, setIsAdmin] = useState(false)

    const resetAppsStates = () => {
        setUserEmployments([])
        setIsManager(false)
        setIsManagerFor({})
        setCanEditShift(false)
        setCanEditShiftFor({})
        setUserInfo({})
        setIsAdmin(false)
    }

    const fetchAndSetEmployments = () => {
        AxiosWithAuth.get('/employments')
            .then(res => {
                setUserEmployments(res.data)
            })
            .catch(() => {
                setUserEmployments([])
            })
    }
    const fetchAndSetUserInfo = () => {
        AxiosWithAuth.get('/users/me')
            .then(user => {
                setUserInfo(user.data)
                setIsAdmin(user.data?.is_admin)
            })
            .catch(() => console.log('401 get /users/me'))
    }
    useEffect(() => {
        fetchAndSetEmployments()
        fetchAndSetUserInfo()
    }, [])

    useEffect(() => {
        const handleEmploymentsChanged = () => {
            fetchAndSetEmployments()
        }
        window.addEventListener(STORE_EMPLOYMENTS_CHANGED_EVENT, handleEmploymentsChanged)
        return () => window.removeEventListener(STORE_EMPLOYMENTS_CHANGED_EVENT, handleEmploymentsChanged)
    }, [])

    useEffect(() => {
        if (userEmployments.length > 0) {
            setIsManager(userEmployments.some(employment => employment.is_manager))
            setCanEditShift(userEmployments.some(employment => employment.can_edit_shift))
            userEmployments.forEach(employment => {
                setIsManagerFor(prev => {
                    return {
                        ...prev,
                        [employment.id]: employment.is_manager
                    }
                })
                setCanEditShiftFor(prev => {
                    return {
                        ...prev,
                        [employment.id]: employment.can_edit_shift
                    }
                })
            })
        }
        const storedUser = parseStoredCurrentUser()
        if (storedUser) {
            setIsAdmin(storedUser.is_admin)
        }
    }, [userEmployments, userInfo])

    const { updateAvailable } = useVersionCheck();

    const handleRefresh = () => {
        window.location.reload();
    }

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <Snackbar
                open={updateAvailable}
                action={<Button onClick={handleRefresh}>再読み込み</Button>}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                message="新しいバージョンが利用可能です。ページを再読み込みしてください。"
            />
            <Router basename={process.env.PUBLIC_URL}>
                <EmploymentsContext.Provider
                    value={{
                        userEmployments,
                        setUserEmployments,
                        isManager,
                        isAdmin,
                        userInfo,
                        isManagerFor,
                        canEditShift,
                        canEditShiftFor,
                        fetchAndSetEmployments,
                        resetAppsStates
                    }}
                >
                    <Switch>
                        <Route exact path="/">
                            <Redirect to={PATHS.home} />
                        </Route>
                        {/*TODO: add home path public route with token */}
                        <Route
                            path={PATHS.login}
                            component={props => {
                                return (
                                    <Login
                                        {...props}
                                        fetchAndSetEmployments={fetchAndSetEmployments}
                                        fetchAndSetUserInfo={fetchAndSetUserInfo}
                                    />
                                )
                            }}
                        />
                        <Route path={PATHS.forgotPassword} component={ForgotPassword} />
                        <Route path={PATHS.resetPassword} component={ResetPassword} />
                        <Route path={PATHS.confirmation} component={EmailConfirmation} />
                        <Route path={PATHS.resendConfirmation} component={ResendConfirmation} />
                        <Route path="/">
                            <Layout>
                        
                                <Route path={PATHS.signup} component={Signup} />
                                <Route path={PATHS.home} component={Home} />
                                <Route path={PATHS.submitShift} component={SubmitShift} />{' '}
                                {/* reverse order for routing to work, will fix with nesting later */}
                                <Route path={PATHS.calendar} component={Calendar} />
                                <Route path={PATHS.months} component={SelectMonth} />
                                <Route path={PATHS.stores} component={SelectStore} />
                                <Route path={PATHS.check} component={CheckFinalizedMonths} />
                                <Route path={PATHS.checkCalendar} component={CheckFinalizedCalendar} />
                                <Route path={PATHS.checkDay} component={CheckShiftDay} />
                                <Route path={PATHS.checkMonthlyShift} component={MonthlyShiftHeader} />
                                <Route path={PATHS.createMonthlyShift} component={MonthlyShiftHeader} />
                                <Route path={PATHS.recurring} component={RecurringShifts} />
                                <Route path={PATHS.userSettings} component={UserSettings} />
                                <Route path={PATHS.managerSettings} component={ManagerSettings} />
                                <Route exact path={PATHS.submittedEmployees} component={SubmittedEmployees} />
                                <Route path={PATHS.createDay} component={CheckShiftDay} />
                                <Route path={PATHS.createCalendar} component={CheckFinalizedCalendar} />
                                <Route exact path={PATHS.managerStores} component={CreateShiftStores} />
                                <Route exact path={PATHS.managerMonths} component={CreateShiftMonths} />
                                <AdminRoute path={PATHS.adminSettings} component={AdminSettings} />
                                <Route path={'/chatScheduler'} component={chatScheduler} />
                                <Route path={PATHS.shiftManagement} component={ShiftManagements} />
                                <Route path={PATHS.feedbackManagement} component={FeedbackManagements} />
                                <Route path={PATHS.feedbackManagementDemo} component={FeedbackManagementsDemo} />
                                <Route path={PATHS.viewOnly.home} component={ViewOnlyHome} />
                            </Layout>
                        </Route>
                    </Switch>
                </EmploymentsContext.Provider>
            </Router>
        </LocalizationProvider>
    )
}

export default App
